import { CurveBounds, EventArrowLayer, EventMarkerLayer } from '@statsbomb/kitbag-datavis';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { unwrappedMarkersDescriptionsAtom } from '@/atoms/eventMarkers';
import { eventLinkAtom } from '@/atoms/navigation';
import { selectVisEventsAtom, selectedVisEventsAtom } from '@/atoms/vis/selection';
import { showMarkerStartAtom } from '@/atoms/visualisation';
import { PITCH_HEIGHT, PITCH_WIDTH } from '@/consts/HeatMap';
import {
  BLOCKED_OUTCOME,
  GOAL_OUTCOME,
  SAVED_SHOT_OUTCOMES,
  SHOT_MAP_MARKER_STROKE_WIDTH,
  SHOT_MARKER_FILL_OPACITY,
} from '@/consts/visualisations';
import { useEventMarkerColour } from '@/hooks/eventMap/useEventMarkerColour';
import { useShotMarkerFill } from '@/hooks/shotMap/useShotMarkerFill';
import { useShotMarkerStroke } from '@/hooks/shotMap/useShotMarkerStroke';
import useGetLinkPath from '@/hooks/useGetLinkPath';
import { useGetPitchRotation } from '@/hooks/useGetPitchRotation';
import { useResetSelectionLayer } from '@/hooks/useResetSelectionLayer';
import { ArrowEvent, MarkerEvent } from '@/types/event';
import { ShotMapEventMarker } from '@/types/visualisation';
import { isNullish } from '@/utils/predicates';
import { getShotMarkerShape, isShotEvent } from '@/utils/visualisations';
import { EventLayerTooltip } from './EventLayerTooltip';

const PITCH_BOUNDS: CurveBounds = { minX: 0, minY: 0, maxX: PITCH_WIDTH, maxY: PITCH_HEIGHT };

export const EventMapLayers = ({
  eventMapLayersRef,
  currentlySelectedVideoId,
  allowInteraction,
  markerEvents,
  arrowEvents,
  useDefaultColor,
}: {
  eventMapLayersRef?: React.Ref<SVGGElement>;
  currentlySelectedVideoId: string | null;
  allowInteraction?: boolean;
  markerEvents: MarkerEvent[];
  arrowEvents: ArrowEvent[];
  useDefaultColor?: boolean;
}) => {
  const selectedVisEvents = useAtomValue(selectedVisEventsAtom);
  const selectVisEvents = useSetAtom(selectVisEventsAtom);
  const markersDescriptions = useAtomValue(unwrappedMarkersDescriptionsAtom);

  const allEvents = [...markerEvents, ...arrowEvents];
  const resetSelectionLayer = useResetSelectionLayer();
  const { to, relative } = useGetLinkPath(eventLinkAtom);
  const navigate = useNavigate();

  const showMarkerStart = useAtomValue(showMarkerStartAtom);

  const theme = useTheme();
  const getShotMarkerFill = useShotMarkerFill();
  const getShotMarkerStroke = useShotMarkerStroke();
  const pitchRotation = useGetPitchRotation(false);
  const shotRotation = pitchRotation === 'VERTICAL' || pitchRotation === 'VERTICAL_REVERSE' ? 90 : undefined;

  const getEventMarkerColour = useEventMarkerColour(currentlySelectedVideoId);
  const nowPlayingEventId = allEvents.find(
    ({ videoEventId, eventId }) => (videoEventId || eventId) === currentlySelectedVideoId,
  )?.eventId;
  const nowPlayingEventIds = nowPlayingEventId ? [nowPlayingEventId] : [];

  const onMarkerClick: React.ComponentProps<typeof EventMarkerLayer>['onMarkerClick'] = allowInteraction
    ? (_, id) => {
        resetSelectionLayer();
        selectVisEvents(
          allEvents.filter(({ eventId }) => eventId === id),
          markersDescriptions.filter(({ eventId }) => eventId === id),
        );
        navigate(to, { relative });
      }
    : undefined;

  const selectedItemIds = selectedVisEvents?.map(({ eventId }) => eventId);

  return (
    <g ref={eventMapLayersRef}>
      <EventMarkerLayer
        onMarkerClick={onMarkerClick}
        markers={markerEvents?.map(marker => {
          const {
            eventId: id,
            startX,
            startY,
            endX,
            endY,
            successFail,
            videoEventId,
            xg,
            obv,
            gkSaveDifficultyXg,
          } = marker;
          const x = showMarkerStart || isNullish(endX) ? startX : endX;
          const y = showMarkerStart || isNullish(endY) ? startY : endY;
          const tooltipObv = isShotEvent(marker) ? undefined : obv;
          const tooltip = allowInteraction ? (
            <EventLayerTooltip eventId={id} obv={tooltipObv} xg={xg} gkSaveDifficultyXg={gkSaveDifficultyXg} />
          ) : null;

          if (isShotEvent(marker)) {
            const shotMarker: ShotMapEventMarker = marker;
            const shotStroke =
              marker.outcome === BLOCKED_OUTCOME
                ? getShotMarkerStroke(shotMarker)
                : theme.colours.visualisations.dataObjects.inverse;
            const isSaved = !!marker.outcome && SAVED_SHOT_OUTCOMES.includes(marker.outcome);
            return {
              id,
              x,
              y,
              shapeName: getShotMarkerShape(shotMarker),
              fill: useDefaultColor ? undefined : getShotMarkerFill(shotMarker),
              fillOpacity: SHOT_MARKER_FILL_OPACITY,
              stroke: useDefaultColor ? undefined : shotStroke,
              strokeWidth: isSaved ? SHOT_MAP_MARKER_STROKE_WIDTH.SAVED : SHOT_MAP_MARKER_STROKE_WIDTH.DEFAULT,
              doubleBorder: marker.outcome === GOAL_OUTCOME,
              rotation: shotRotation,
              tooltip,
            };
          }

          return {
            id,
            x,
            y,
            fill: useDefaultColor ? undefined : getEventMarkerColour(videoEventId || id, successFail, xg, obv),
            shapeName: 'CIRCLE',
            tooltip,
          };
        })}
        selectedItemIds={selectedItemIds}
        highlightedItemIds={nowPlayingEventIds}
        isHighlightNowPlaying={!!nowPlayingEventId}
      />
      <EventArrowLayer
        onMarkerClick={onMarkerClick}
        arrows={arrowEvents?.map(arrow => {
          const {
            eventId: id,
            startX,
            startY,
            endX,
            endY,
            successFail,
            videoEventId,
            xg,
            obv,
            gkSaveDifficultyXg,
            curveDirection,
          } = arrow;
          const tooltipObv = isShotEvent(arrow) ? undefined : obv;
          return {
            id,
            startX,
            startY,
            endX,
            endY,
            stroke: useDefaultColor ? undefined : getEventMarkerColour(videoEventId || id, successFail, xg, obv),
            arrowHeadName: 'EQUILATERAL',
            curveDirection,
            bounds: PITCH_BOUNDS,
            tooltip: allowInteraction ? (
              <EventLayerTooltip eventId={id} obv={tooltipObv} xg={xg} gkSaveDifficultyXg={gkSaveDifficultyXg} />
            ) : null,
          };
        })}
        selectedItemIds={selectedItemIds}
        highlightedItemIds={nowPlayingEventIds}
        isHighlightNowPlaying={!!nowPlayingEventId}
      />
    </g>
  );
};
